import React, { useState } from 'react';
import { useTranslation } from "react-i18next";
import './DeviceOrder.css';
import { submitDeviceOrder } from '../../services/deviceService';
import Sidebar from "../shared/Sidebar";
import Header from "../shared/Header";
import { useTheme } from "../../contexts/ThemeContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getItem, logout } from "../../utils/authStorage";
import CustomSelect from "../shared/CustomSelect";
import { DEVICE_TYPE_ORDER_OPTIONS } from "../shared/selectOptions";


function DeviceOrder() {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [type, setType] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();
    const username = getItem("username");
    const role = (getItem("role") || "operator").toLowerCase();

    const handleSignOut = () => {
        logout();
    };

    const hasRoleFn = (allowedRoles) => {
        const storedRole = (getItem("role") || '').toLowerCase();
        return allowedRoles.flat().some(r => typeof r === 'string' && r.toLowerCase() === storedRole);
    };

    const handleNameChange = (e) => {
        const input = e.target.value;
        if (/^[a-zA-Z0-9 ]*$/.test(input)) {
            setName(input);
            setError('');
        } else {
            setError(t("validation.deviceNameError"));
        }
    };

    const handleSubmit = async () => {
        const trimmedName = name.trim();
        const trimmedType = type.trim();

        if (!trimmedName || !trimmedType) {
            setError(t("validation.bothFieldsRequired"));
            return;
        }

        setError('');
        setLoading(true);
        const result = await submitDeviceOrder(trimmedName, trimmedType);

        setLoading(false);
        if (!result.success) {
            setError(result.error);
            toast.error(result.error || t("label.deviceOrderFailed"));
        } else {
            toast.success(t("label.deviceOrderSuccess"));
            setName('');
            setType('');
        }
    };

    return (
        <div className={`device-order-page ${isDarkMode ? "dark" : "light"}`}>
            <Sidebar
                collapsed={collapsed}
                onToggleCollapse={() => setCollapsed(prev => !prev)}
                isDarkMode={isDarkMode}
                username={username}
                role={role}
                onLogout={handleSignOut}
                hasRole={hasRoleFn}
            />

            <div className="main-content">
                {/* Header matching other refactored pages */}
                <Header title={t("nav.addDevice")} />
                <div className="main-divider" />

                {loading && (
                    <div className="device-order-loading">
                        <div className="device-order-spinner"></div>
                        <p>{t("label.processingRequest")}</p>
                    </div>
                )}

                <div className="device-order-container">
                    <div className="device-order-card">
                        <h2 className="device-order-title">{t("label.newDeviceOrder")}</h2>
                        <p className="device-order-subtitle">
                            {t("label.newDeviceOrderSubtitle")}
                        </p>

                        {error && (
                            <div className="device-order-error">{error}</div>
                        )}

                        <div className="form-group">
                            <label htmlFor="device-name" className="form-label">
                                {t("label.deviceName")} <span className="required-asterisk">*</span>
                            </label>
                            <input
                                id="device-name"
                                type="text"
                                className="form-input"
                                placeholder={t("placeholder.enterDeviceName")}
                                value={name}
                                onChange={handleNameChange}
                                disabled={loading}
                            />
                            <p className="form-help-text">
                                {t("label.nameHelpText")}
                            </p>
                        </div>

                        <div className="form-group">
                            <label htmlFor="device-type" className="form-label">
                                {t("label.deviceType")} <span className="required-asterisk">*</span>
                            </label>
                            <div className="select-wrapper">
                                <CustomSelect
                                    value={type}
                                    onChange={setType}
                                    options={DEVICE_TYPE_ORDER_OPTIONS.map(o => ({ ...o, label: t(o.labelKey) }))}
                                />
                            </div>
                            <p className="form-help-text">
                                {t("label.typeHelpText")}
                            </p>
                        </div>

                        <div className="form-actions">
                            <button
                                type="button"
                                className="btn-cancel"
                                onClick={() => navigate("/device-list")}
                                disabled={loading}
                            >
                                {t("button.cancel")}
                            </button>
                            <button
                                type="button"
                                className="btn-submit"
                                onClick={handleSubmit}
                                disabled={loading || !name.trim() || !type}
                            >
                                {t("button.submitOrder")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DeviceOrder;